/**
 * The leading-edge lane rule.
 *
 * A set that mixes corpora, jurisdictions, question shapes or asset kinds owes
 * the reader a way to see which lane a row came from without reading it. That
 * marker is a position on the accent ramp, never a new hue: the system has one
 * brand colour, and eighteen pillars inventing eighteen more is the fastest
 * way to lose it.
 *
 * The rule is positioned with logical properties, so a right-to-left record
 * gets its lane on the correct edge without a second component.
 *
 * The rule has always been out of flow and has never cost the wrapped element a
 * pixel. What did cost it 10px was the GUTTER the lane held its content clear of
 * that rule by, taken as `padding-inline-start` — and on a row that is 10px out
 * of the 548px §3.3 measures, which is why two pillars chose not to draw the
 * rule at a row's leading edge at all. `clearance` is that choice made in the
 * open: a holder with its own inline padding (a row has 13px) already has
 * somewhere for the rule to sit.
 *
 * `Holder` moves the rule INTO the holder's own leading edge, which is also
 * where `CresultcardRow` draws its two hover layers — a 2px accent bar and a
 * gradient wash, both at `inset-inline-start: 0`. Under `Reserved` those layers
 * sat 10px inboard of the rule and never touched it; under `Holder` they land
 * on the same pixels and paint over it, because they come later in tree order.
 * A constant-opacity bar over the ramp is the lane mark erased for as long as
 * the pointer is on the row, so the lane publishes where its rule ends and the
 * row starts its own accent layers from there.
 */
"use client";
import type { CSSProperties, ReactElement, ReactNode } from "react";
import {
  CRC_ACCENT,
  CRC_LANE_OPACITY,
  CRC_VISUALLY_HIDDEN,
} from "./cresultcard.tokens";
import {
  CresultcardLaneClearance,
  CresultcardLaneSlot,
} from "./cresultcard.types";

const LANE_RULE_WIDTH = 2;
const LANE_INSET = 10;
/**
 * The dark the rule keeps between itself and the holder's own leading-edge
 * ornament. Two accent hairlines flush against each other read as one 4px mark
 * with a seam; four pixels apart they read as two marks, which is what they are.
 */
const LANE_ROW_ACCENT_GAP = 4;

/**
 * `Holder` is 0 because the rule is absolutely positioned either way: the whole
 * inset IS the gutter, so declining the gutter is declining the inset.
 */
const LANE_CLEARANCE_INSET: Record<CresultcardLaneClearance, number> = {
  [CresultcardLaneClearance.Reserved]: LANE_INSET,
  [CresultcardLaneClearance.Holder]: 0,
};

/**
 * Where a holder's own leading-edge accent has to start so the rule survives it.
 *
 * `Reserved` is 0: the rule sits in the lane's manufactured gutter, OUTSIDE the
 * holder's box, so nothing the holder paints at its leading edge can reach it.
 * `Holder` is the rule plus its gap, because there the two share an edge.
 */
const LANE_ROW_ACCENT_INSET: Record<CresultcardLaneClearance, number> = {
  [CresultcardLaneClearance.Reserved]: 0,
  [CresultcardLaneClearance.Holder]: LANE_RULE_WIDTH + LANE_ROW_ACCENT_GAP,
};

/**
 * Declared as a type rather than cast, because a style object with an unknown
 * key is exactly the shape a typo hides in.
 */
interface CresultcardLaneStyle extends CSSProperties {
  readonly "--crc-row-accent-inset": string;
}

interface CresultcardLaneProps {
  readonly children: ReactNode;
  /** Which position on the accent ramp this lane holds. */
  readonly lane: CresultcardLaneSlot;
  /** What the lane IS, for the reader and for assistive technology. */
  readonly label: string;
  /**
   * Where the rule's clearance comes from. `Reserved` — the lane's own 10px —
   * is the default, because it is the only reading that is safe around content
   * with no inline padding of its own.
   */
  readonly clearance?: CresultcardLaneClearance;
}

export function CresultcardLane({
  children,
  lane,
  label,
  clearance = CresultcardLaneClearance.Reserved,
}: CresultcardLaneProps): ReactElement {
  const laneStyle: CresultcardLaneStyle = {
    position: "relative",
    minWidth: 0,
    paddingInlineStart: LANE_CLEARANCE_INSET[clearance],
    "--crc-row-accent-inset": `${String(LANE_ROW_ACCENT_INSET[clearance])}px`,
  };

  return (
    <div
      data-el="Cresultcard/Lane/Root"
      style={laneStyle}
    >
      <span
        aria-hidden
        style={{
          position: "absolute",
          insetBlock: 0,
          insetInlineStart: 0,
          width: LANE_RULE_WIDTH,
          borderRadius: LANE_RULE_WIDTH,
          backgroundColor: CRC_ACCENT,
          opacity: CRC_LANE_OPACITY[lane],
        }}
      />
      <span
        style={CRC_VISUALLY_HIDDEN}
      >
        {label}
      </span>
      {children}
    </div>
  );
}
